import React, { FunctionComponent } from 'react';
import Translate from 'ui/atoms/translate';
import { PersonType } from 'ui/types/person';
import Section from 'ui/atoms/section';
import Grid, { Col } from 'ui/atoms/grid';
import Header from 'ui/atoms/header';
import { CreateAccountForm } from 'libraries/wizard/components/account-setup/create-account/helpers';
import { useServerConfigSelector } from 'core/config/hooks';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import useCountries from 'src/hooks/use-countries';

export interface NaturalPersonProps {
  personType: PersonType;
  naturalBirthCountry: string | undefined;
  nationality: string | undefined;
  naturalCountry: string | undefined;
}

const NaturalPerson: FunctionComponent<NaturalPersonProps> = ({
  personType,
  naturalBirthCountry,
  nationality,
  naturalCountry,
}) => {
  const { config } = useServerConfigSelector();

  const isLargeScreen = useIsMedia([DEVICES.lTablet, DEVICES.desktops]);
  const countriesList = useCountries();

  return (
    <Section>
      <Section spacing="small">
        <Header size="small" spacing="medium">
          <Translate name="createAccount.headers.individual.personalInformation" />
        </Header>
        <Grid>
          <Col leftCol width={6} tablet={12} phone={12}>
            <CreateAccountForm.Group name="naturalForename" required={true}>
              <CreateAccountForm.Input autoFocus={true} />
            </CreateAccountForm.Group>
          </Col>
          <Col rightCol width={6} tablet={12} phone={12}>
            <CreateAccountForm.Group name="naturalSurname" required={true}>
              <CreateAccountForm.Input />
            </CreateAccountForm.Group>
          </Col>
        </Grid>
        <Grid>
          <Col leftCol width={6} tablet={12} phone={12}>
            <CreateAccountForm.Group name="naturalBirthDate" required={true}>
              <CreateAccountForm.DateInput max={new Date()} />
              {CreateAccountForm.Validators.PastDate()}
              {CreateAccountForm.Validators.Over18()}
            </CreateAccountForm.Group>
          </Col>
          <Col rightCol width={6} tablet={12} phone={12}>
            <CreateAccountForm.Group name="naturalBirthPlace" required={true} occupyHiddenLabelSpace={isLargeScreen}>
              <CreateAccountForm.Input />
            </CreateAccountForm.Group>
          </Col>
        </Grid>
        <Grid>
          <Col leftCol width={6} tablet={12} phone={12}>
            <CreateAccountForm.Group name="naturalBirthCountry" required={true}>
              <CreateAccountForm.Select options={countriesList} value={naturalBirthCountry}></CreateAccountForm.Select>
            </CreateAccountForm.Group>
          </Col>
          <Col rightCol width={6} tablet={12} phone={12}>
            <CreateAccountForm.Group name="nationality" required={true}>
              <CreateAccountForm.Select options={countriesList} value={nationality}></CreateAccountForm.Select>
            </CreateAccountForm.Group>
          </Col>
        </Grid>
      </Section>
      {personType === PersonType.Natural && (
        <>
          <Header spacing="medium">
            <Translate name="createAccount.headers.individual.contactInformation" />
          </Header>
          <CreateAccountForm.Group name="naturalStreet" required={true}>
            <CreateAccountForm.Input />
          </CreateAccountForm.Group>
          <Grid>
            <Col leftCol width={4} tablet={12} phone={12}>
              <CreateAccountForm.Group name="naturalZip" required={true}>
                <CreateAccountForm.Input />
              </CreateAccountForm.Group>
            </Col>
            <Col rightCol width={8} tablet={12} phone={12}>
              <CreateAccountForm.Group name="naturalCity" required={true}>
                <CreateAccountForm.Input />
              </CreateAccountForm.Group>
            </Col>
          </Grid>
          <CreateAccountForm.Group name="naturalCountry" required={true}>
            <CreateAccountForm.Select options={countriesList} value={naturalCountry}></CreateAccountForm.Select>
          </CreateAccountForm.Group>
        </>
      )}
      <CreateAccountForm.Group name="naturalPhone" required={config.telephoneNumberRequired}>
        <CreateAccountForm.Input />
        {CreateAccountForm.Validators.PhoneNumber()}
      </CreateAccountForm.Group>
    </Section>
  );
};

export default NaturalPerson;
